import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Phone,
  Mail,
  Globe,
  Camera,
  Trash2,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  Building,
} from 'lucide-react';
import { WorkspaceUser } from '../types';
import { uploadEmployeeAvatar } from '../services/firebase';
import { updateUserDataInFirestore } from '../services/signaturePersistenceService';

interface EditUserModalProps {
  isOpen: boolean;
  user: WorkspaceUser | null;
  domain: string;
  companyPhoneDefault?: string;
  companyWebsiteDefault?: string;
  companyAddressDefault?: string;
  onClose: () => void;
  onSave: (updatedUser: WorkspaceUser) => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  user,
  domain,
  companyPhoneDefault = '+48 22 123 45 67',
  companyWebsiteDefault = 'https://cerebrolabs.io',
  companyAddressDefault = 'ul. Prosta 20, 00-850 Warszawa',
  onClose,
  onSave,
}) => {
  if (!isOpen || !user) return null;

  const [fullName, setFullName] = useState(user.fullName || '');
  const [jobTitle, setJobTitle] = useState(user.jobTitle || '');
  const [department, setDepartment] = useState(user.department || '');

  // Phone hierarchy
  const [useCustomPhone, setUseCustomPhone] = useState(user.useCustomPhone ?? true);
  const [phone, setPhone] = useState(user.phone || '');
  const [showMobile, setShowMobile] = useState(user.showMobile ?? false);
  const [mobile, setMobile] = useState(user.mobile || '');

  // Email alias
  const [emailAlias, setEmailAlias] = useState(user.emailAlias || '');

  // Website override
  const [useCustomWebsite, setUseCustomWebsite] = useState(user.useCustomWebsite ?? false);
  const [customWebsiteUrl, setCustomWebsiteUrl] = useState(user.customWebsiteUrl || '');

  // Address override
  const [useCustomAddress, setUseCustomAddress] = useState(user.useCustomAddress ?? false);
  const [customAddress, setCustomAddress] = useState(user.customAddress || '');

  // Avatar
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || '');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Status
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Sync state if user prop changes
  useEffect(() => {
    if (user) {
      setFullName(user.fullName || '');
      setJobTitle(user.jobTitle || '');
      setDepartment(user.department || '');
      setUseCustomPhone(user.useCustomPhone ?? Boolean(user.phone));
      setPhone(user.phone || '');
      setShowMobile(user.showMobile ?? Boolean(user.mobile));
      setMobile(user.mobile || '');
      setEmailAlias(user.emailAlias || '');
      setUseCustomWebsite(user.useCustomWebsite ?? Boolean(user.customWebsiteUrl));
      setCustomWebsiteUrl(user.customWebsiteUrl || '');
      setUseCustomAddress(user.useCustomAddress ?? Boolean(user.customAddress));
      setCustomAddress(user.customAddress || '');
      setAvatarUrl(user.avatarUrl || '');
      setErrorMsg(null);
      setSuccessMsg(null);
    }
  }, [user]);

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMsg('Wybrany plik musi być obrazem (PNG, JPG, WebP, SVG).');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg('Rozmiar pliku nie może przekraczać 5 MB.');
      return;
    }

    setIsUploadingAvatar(true);
    setErrorMsg(null);

    try {
      const downloadUrl = await uploadEmployeeAvatar(file, domain, user.id);
      setAvatarUrl(downloadUrl);
      setSuccessMsg('Pomyślnie wgrano zdjęcie do chmury!');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      console.warn('Błąd uploadu awatara, wczytuję podgląd Data URL:', err);
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result as string;
        if (dataUrl) {
          setAvatarUrl(dataUrl);
          setSuccessMsg('Wczytano zdjęcie lokalnie.');
          setTimeout(() => setSuccessMsg(null), 3000);
        }
      };
      reader.readAsDataURL(file);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = () => {
    setAvatarUrl('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMsg(null);

    const targetUserId = (user.id && user.id.trim()) || user.primaryEmail || 'user';
    const targetDomain = (domain && domain.trim()) || (user.primaryEmail ? user.primaryEmail.split('@')[1] : 'cerebrolabs.io');

    const updatedUser: WorkspaceUser = {
      ...user,
      id: targetUserId,
      fullName: fullName.trim(),
      jobTitle: jobTitle.trim(),
      department: department.trim(),
      useCustomPhone,
      phone: useCustomPhone ? phone.trim() : '',
      showMobile,
      mobile: showMobile ? mobile.trim() : '',
      emailAlias: emailAlias.trim(),
      useCustomWebsite,
      customWebsiteUrl: useCustomWebsite ? customWebsiteUrl.trim() : '',
      useCustomAddress,
      customAddress: useCustomAddress ? customAddress.trim() : '',
      avatarUrl: avatarUrl.trim(),
    };

    try {
      await updateUserDataInFirestore(targetDomain, targetUserId, updatedUser);
      onSave(updatedUser);
      onClose();
    } catch (err: any) {
      console.error('Błąd zapisu profilu pracownika:', err);
      setErrorMsg(`Nie udało się zapisać danych: ${err.message || 'Błąd Firestore'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Avatar initials helper
  const initials =
    fullName
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'CS';

  return (
    <div className="modal-overlay" style={{ zIndex: 110 }}>
      <div className="modal-card" style={{ maxWidth: 640, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, rgba(2, 132, 199, 0.08) 0%, transparent 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: 'rgba(2, 132, 199, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#38bdf8',
              }}
            >
              <User size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0B192C' }}>
                Edytuj profil pracownika
              </h3>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {user.primaryEmail}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
            style={{ padding: 6, borderRadius: 8 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Form Body - Scrollable */}
        <form onSubmit={handleSave} style={{ overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {errorMsg && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#f87171',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <AlertCircle size={16} />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.12)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#6ee7b7',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <CheckCircle2 size={16} />
              <span>{successMsg}</span>
            </div>
          )}

          {/* 1. SEKCJA: ZDJĘCIE PROFILOWE / AWATAR PRACOWNIKA */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0B192C', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Camera size={16} color="#38bdf8" />
                <span>Zdjęcie profilowe / Awatar (konkretny pracownik)</span>
              </div>
              <span style={{ fontSize: 10, color: '#64748b' }}>Firebase Storage</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={fullName}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #0284c7',
                    boxShadow: '0 0 10px rgba(2, 132, 199, 0.3)',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: '#eff6ff',
                    border: '2px dashed #93c5fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0066FF',
                    fontWeight: 700,
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexGrow: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label
                    className="btn btn-primary"
                    style={{
                      padding: '6px 12px',
                      fontSize: 12,
                      cursor: isUploadingAvatar ? 'not-allowed' : 'pointer',
                      margin: 0,
                    }}
                  >
                    {isUploadingAvatar ? (
                      <>
                        <Loader2 size={13} className="animate-spin" />
                        <span>Wgrywanie...</span>
                      </>
                    ) : (
                      <>
                        <Camera size={13} />
                        <span>{avatarUrl ? 'Zmień zdjęcie' : 'Wgraj zdjęcie'}</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      onChange={handleAvatarFileChange}
                      disabled={isUploadingAvatar}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {avatarUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      className="btn btn-outline"
                      style={{ padding: '6px 12px', fontSize: 12, color: '#f87171', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                    >
                      <Trash2 size={13} />
                      <span>Usuń</span>
                    </button>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4 }}>
                  Ścieżka w chmurze: <code style={{ color: '#64748b' }}>/tenants/{domain}/users/{user.id}/avatar.png</code>.
                  W szablonie wystarczy włączyć przełącznik wyświetlania awatarów.
                </div>
              </div>
            </div>
          </div>

          {/* 2. SEKCJA: DANE PERSONALNE */}
          <div className="modal-grid-2">
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 5, fontWeight: 600 }}>
                Imię i Nazwisko *
              </label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input-control"
                placeholder="np. Jan Kowalski"
              />
            </div>

            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 5, fontWeight: 600 }}>
                Stanowisko / Rola
              </label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="input-control"
                placeholder="np. Senior Solutions Architect"
              />
            </div>

            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 5, fontWeight: 600 }}>
                Dział / Zespół
              </label>
              <input
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="input-control"
                placeholder="np. Cloud Engineering"
              />
            </div>
          </div>

          {/* 3. SEKCJA: TELEFONY (HIERARCHIA OVERRIDE / FALLBACK) */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0B192C', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Phone size={15} color="#38bdf8" />
              <span>Hierarchia numerów telefonów</span>
            </div>

            {/* Opcja telefonu firmy vs własny numer */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 12,
                  color: '#0B192C',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={useCustomPhone}
                  onChange={(e) => setUseCustomPhone(e.target.checked)}
                />
                <span>Własny numer telefonu bezpośredniego (Direct)</span>
              </label>

              {useCustomPhone ? (
                <div style={{ paddingLeft: 28 }}>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="input-control"
                    placeholder="np. +48 22 876 54 32"
                    style={{ fontSize: 12 }}
                  />
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    Ten unikalny numer zostanie użyty w stopce tego pracownika.
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    paddingLeft: 28,
                    fontSize: 11,
                    color: '#34d399',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Building size={13} />
                  <span>Dziedziczy ogólny telefon firmy z szablonu: <strong>{companyPhoneDefault}</strong></span>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 12,
                  color: '#0B192C',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={showMobile}
                  onChange={(e) => setShowMobile(e.target.checked)}
                />
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Smartphone size={14} color="#38bdf8" />
                  <span>Pokazuj numer komórkowy w stopce</span>
                </span>
              </label>

              {showMobile && (
                <div style={{ paddingLeft: 28 }}>
                  <input
                    type="text"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    className="input-control"
                    placeholder="np. +48 601 234 567"
                    style={{ fontSize: 12 }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* 4. SEKCJA: EMAIL I ALIAS */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0B192C', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={15} color="#38bdf8" />
              <span>Adres Email & Alias</span>
            </div>

            <div className="modal-grid-2">
              <div>
                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                  Konto Google Workspace (stałe)
                </label>
                <input
                  type="text"
                  disabled
                  value={user.primaryEmail}
                  className="input-control"
                  style={{ opacity: 0.7, background: '#f1f5f9', cursor: 'not-allowed', fontSize: 12 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
                  Opcjonalny alias email w stopce
                </label>
                <input
                  type="email"
                  value={emailAlias}
                  onChange={(e) => setEmailAlias(e.target.value)}
                  className="input-control"
                  placeholder="np. architekci@cerebrolabs.io"
                  style={{ fontSize: 12 }}
                />
              </div>
            </div>
          </div>

          {/* 5. SEKCJA: NADPISYWANIE WWW & ADRESU ODDZIAŁU */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0B192C', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Globe size={15} color="#38bdf8" />
              <span>Strona WWW & Adres (Opcjonalne nadpisanie szablonu)</span>
            </div>

            {/* WWW */}
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 12,
                  color: '#0B192C',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginBottom: 6,
                }}
              >
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={useCustomWebsite}
                  onChange={(e) => setUseCustomWebsite(e.target.checked)}
                />
                <span>Dedykowany landing page / profil pracownika (zamiast {companyWebsiteDefault})</span>
              </label>

              {useCustomWebsite && (
                <div style={{ paddingLeft: 28 }}>
                  <input
                    type="url"
                    value={customWebsiteUrl}
                    onChange={(e) => setCustomWebsiteUrl(e.target.value)}
                    className="input-control"
                    placeholder="https://cerebrolabs.io/team/jan-kowalski"
                    style={{ fontSize: 12 }}
                  />
                </div>
              )}
            </div>

            {/* Adres */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 12,
                  color: '#0B192C',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginBottom: 6,
                }}
              >
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={useCustomAddress}
                  onChange={(e) => setUseCustomAddress(e.target.checked)}
                />
                <span>Własny adres oddziału pracownika (zamiast {companyAddressDefault})</span>
              </label>

              {useCustomAddress && (
                <div style={{ paddingLeft: 28 }}>
                  <input
                    type="text"
                    value={customAddress}
                    onChange={(e) => setCustomAddress(e.target.value)}
                    className="input-control"
                    placeholder="np. ul. Fabryczna 5, 31-553 Kraków"
                    style={{ fontSize: 12 }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer buttons */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              paddingTop: 8,
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={isSaving}
            >
              Anuluj
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              <span>{isSaving ? 'Zapisywanie...' : 'Zapisz dane pracownika'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
